import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Heart, GraduationCap, Stethoscope, MapPin } from 'lucide-react';
export function AboutPage() {
  return <>
      <Section className="py-20" background="offwhite" data-id="element-226">
        <div className="text-center mb-12" data-id="element-227">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-228">
            About Farasha Medical Aid
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-229">
            Two students saw a need others missed.
          </p>
        </div>
      </Section>

      <Section background="white" data-id="element-230">
        <div className="max-w-4xl mx-auto mb-16" data-id="element-231">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-232">
            Our Story
          </h2>
          <div className="space-y-4 text-lg font-inter text-neutral-charcoal" data-id="element-233">
            <p data-id="element-234">
              In Egypt's Eastern Desert, ancient monasteries have preserved
              Christian faith and culture for nearly two millennia. These aren't
              museums—they're living communities where monks maintain centuries
              of tradition while serving surrounding villages. And they're
              struggling.
            </p>
            <p data-id="element-235">
              Farasha Medical Aid began when we visited these communities and
              discovered a striking gap: dedicated healthcare practitioners
              working with inadequate supplies, serving populations invisible to
              Egypt's urban-focused healthcare system. We returned to America
              knowing we could help.
            </p>
            <p data-id="element-236">
              The name "Farasha" (فراشة) means "butterfly" in
              Arabic—representing transformation and hope. We're building
              something small that creates outsized impact: a reliable supply
              chain connecting American medical resources with Egyptian
              communities that need them most.
            </p>
          </div>
        </div>
      </Section>

      <Section background="sand" data-id="element-237">
        <div className="mb-12" data-id="element-238">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-12 text-center" data-id="element-239">
            Meet Our Founders
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-240">
            <div className="bg-white p-8 rounded-xl shadow-md" data-id="element-241">
              <div className="mb-6" data-id="element-242">
                <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-id="element-243">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nbJVafVZgRcxD2AxVMVQyD/0225681B-F332-4CF8-BE10-DDAFB26CF5D9_1_105_c.jpg" alt="Natasha Khalil, Co-Founder of Farasha Medical Aid" className="w-full h-64 object-cover" data-id="element-244" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-245">
                  Natasha Khalil
                </h3>
                <p className="font-inter text-primary-royal-blue mb-4" data-id="element-246">
                  Co-Founder & Executive Director
                </p>
              </div>
              <div className="space-y-3 text-base font-inter text-neutral-charcoal" data-id="element-247">
                <p data-id="element-248">
                  Natasha Khalil is a first-generation Egyptian-Mexican American
                  from Los Angeles, whose immigrant parents instilled in her the
                  values of resilience, humility, and service.
                </p>
                <p data-id="element-249">
                  Inspired by her father's experiences in Egypt, where
                  healthcare access was often limited, Natasha committed herself
                  to advancing health with dignity and equity for all.
                </p>
                <p data-id="element-250">
                  At Loyola Marymount University, she earned a B.A. in
                  Psychology, studying the intersection of mental health,
                  physical wellness, and social conditions that shape health
                  outcomes. Currently preparing for medical school, she leads
                  Farasha's logistics and monastery partnerships.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md" data-id="element-251">
              <div className="mb-6" data-id="element-252">
                <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-id="element-253">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kaALpsMzDtAjX55veVz28S/3EF4819E-249B-4E37-B8FE-31CEA4263BEE_1_105_c.jpg" alt="Nicole Khalil, Co-Founder of Farasha Medical Aid" className="w-full h-64 object-cover" data-id="element-254" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-255">
                  Nicole Khalil
                </h3>
                <p className="font-inter text-primary-royal-blue mb-4" data-id="element-256">
                  Co-Founder & Communications Director
                </p>
              </div>
              <div className="space-y-3 text-base font-inter text-neutral-charcoal" data-id="element-257">
                <p data-id="element-258">
                  Nicole Khalil is a student at Loyola Marymount University
                  pursuing a Bachelor of Arts in English and minor in
                  Theological Studies.
                </p>
                <p data-id="element-259">
                  While pursuing her bachelor's degree, Nicole noticed that
                  oftentimes the most important stories are suppressed,
                  overshadowed, or entirely forgotten by dominant cultural
                  narratives.
                </p>
                <p data-id="element-260">
                  Through her blog posts, she strives to counteract this trend
                  by revealing the trials and tribulations of the Coptic people.
                  She hopes, in doing so, her posts will incite cultural
                  awareness and meaningful action toward the pursuit of change.
                </p>
                <p data-id="element-261">
                  Nicole leads Farasha's storytelling, educational content, and
                  donor communications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section background="white" data-id="element-262">
        <div className="max-w-4xl mx-auto" data-id="element-263">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6 text-center" data-id="element-264">
            The Meaning Behind Farasha
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-265">
            <div className="space-y-4 text-lg font-inter text-neutral-charcoal" data-id="element-266">
              <p data-id="element-267">
                "Farasha" means "butterfly" in Arabic, honoring Natasha's late
                grandmother, Kamilia. The name reflects our belief that focused,
                evidence-based interventions can create meaningful
                transformation—just as a butterfly effect can generate
                significant change from small actions.
              </p>
              <p data-id="element-268">
                Our logo combines the butterfly symbol with the medical
                caduceus, representing our commitment to healthcare
                transformation and our Egyptian heritage.
              </p>
            </div>
            <div className="flex justify-center items-center" data-id="element-269">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vJ4LnEANA2qYvkkH1pNfQy/design-d5307099-5cbb-4264-b386-3c96f879a243.png" alt="Farasha Medical Aid Logo" className="h-64 w-auto" data-id="element-270" />
            </div>
          </div>
        </div>
      </Section>

      <Section background="deep-blue" data-id="element-271">
        <div className="text-center" data-id="element-272">
          <h2 className="text-3xl font-playfair font-bold mb-6" data-id="element-273">
            Join Our Founding Mission
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-274">
            Help us deliver our inaugural shipment of medical supplies to
            monastery communities in Egypt.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-275">
            <Button href="https://www.paypal.com/biz/profile/FarashaMedicalAid" size="lg" data-id="element-276">
              Donate Now
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-277">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}